import type {
  NewsletterPage,
  NewsletterUpsertRequest,
} from '../newsletter.types';

export interface NewsletterAdminFormState
  extends NewsletterUpsertRequest {
  id: string | null;
}

export interface NewsletterStatusViewModel {
  label: 'Draft' | 'Scheduled' | 'Live';
  color: string;
}

export interface NewsletterAdminListItemViewModel {
  page: NewsletterPage;
  startAtLabel: string;
  status: NewsletterStatusViewModel;
  updatedAtLabel: string;
}

export const formatNewsletterDateTime = (value: string) => {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

export const toLocalNewsletterInputValue = (value?: string) => {
  const date = value ? new Date(value) : new Date();
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
};

export const normalizeNewsletterStartAt = (value: string) => {
  const date = value ? new Date(value) : new Date();
  return isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
};

export const createInitialNewsletterFormState =
  (): NewsletterAdminFormState => ({
    id: null,
    title: '',
    content: '',
    startAt: toLocalNewsletterInputValue(),
    published: false,
  });

export const toNewsletterEditFormState = (
  page: NewsletterPage,
): NewsletterAdminFormState => ({
  id: page.id,
  title: page.title,
  content: page.content,
  startAt: toLocalNewsletterInputValue(page.startAt),
  published: page.published,
});

export const toNewsletterUpsertPayload = (
  formState: NewsletterAdminFormState,
): NewsletterUpsertRequest => ({
  title: formState.title.trim(),
  content: formState.content,
  startAt: normalizeNewsletterStartAt(formState.startAt),
  published: formState.published,
});

export const getNewsletterStatus = (
  page: NewsletterPage,
): NewsletterStatusViewModel => {
  const now = new Date();
  const start = new Date(page.startAt);

  if (!page.published) {
    return { label: 'Draft', color: 'bg-gray-100 text-gray-700' };
  }

  if (start > now) {
    return { label: 'Scheduled', color: 'bg-amber-100 text-amber-800' };
  }

  return { label: 'Live', color: 'bg-emerald-100 text-emerald-700' };
};

export const sortNewsletterPagesByStartAt = (
  pages: NewsletterPage[],
) => {
  return pages
    .slice()
    .sort(
      (left, right) =>
        new Date(right.startAt).getTime() -
        new Date(left.startAt).getTime(),
    );
};

export const getNewsletterAdminListItems = (
  pages: NewsletterPage[],
): NewsletterAdminListItemViewModel[] => {
  return sortNewsletterPagesByStartAt(pages).map((page) => ({
    page,
    startAtLabel: formatNewsletterDateTime(page.startAt),
    status: getNewsletterStatus(page),
    updatedAtLabel: formatNewsletterDateTime(page.updatedAt),
  }));
};
